/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
document.addEventListener("DOMContentLoaded", () => {
  (function () {
    /**
     * This code snippet works in conjuction with uploadery / infinite options app by ShopPad Inc.:
     * https://apps.shopify.com/custom-options
     * There is currently a bug where the input fields' orders are reversed
     * This code snippets attempts to change the order of input fields so they're correctly traversed in orders
     * Triggered inside product-form.liquid
     */
    // Select the product form to target
    const productForm = document.querySelector(".product-form");
    const config = { attributes: true, childList: true, subtree: true };

    function handleProductOptionOrder() {
      const productBuyForm = document.querySelector(
        '.product-form__buy-buttons form[action="/cart/add"]'
      );
      const items = productBuyForm.querySelectorAll(
        'input[name^="properties"]'
      );
      Array.from(items)
        .sort(function (a, b) {
          // Split at separator - which is the naming convention used by Uploadery
          const c = a.id.split("-").find((a) => Number(a));
          const d = b.id.split("-").find((a) => Number(a));
          return c - d;
        })
        .forEach((input) => {
          input.classList.add("processed");
          productBuyForm.appendChild(input);
        });
    }

    /**
     * This code snippet works in conjuction with uploadery / infinite options app by ShopPad Inc.:
     * https://apps.shopify.com/custom-options
     * Helper script to customize product personalizations
     * Specifically allows dropdowns to be automatically removed on load
     * Automatically displays input number based on variant selection
     */
    function handleProductOptionDisplay() {
      const infiniteContainer = document.querySelector(
        "#infiniteoptions-container"
      );
      const swatchInputs = document.querySelectorAll(
        ".block-swatch-list .block-swatch input[data-number-value]"
      );
      const optionSelector =
        infiniteContainer.querySelector(".spb-select select");

      if (!infiniteContainer || !swatchInputs.length || !optionSelector) return;
      const setSelectedOption = (input) => {
        if (!input) return;
        const swatchInputValue = input.getAttribute("data-number-value");
        const swatchInputIndex = Number(swatchInputValue) + 1; // Uploadery is nth + 1 index based due to first option being a label
        const selectedOption = optionSelector.querySelector(
          `option:nth-child(${swatchInputIndex})`
        );
        if (selectedOption) {
          optionSelector.value = selectedOption.value;
          optionSelector.dispatchEvent(new Event("change"));
        }

        // There is a bug on uploadery where the first upload option is not displaying
        if (swatchInputIndex == 2) {
          var formDiv = infiniteContainer.children[swatchInputIndex];
          if (formDiv?.firstChild?.tagName == "SPAN") {
            formDiv.removeAttribute("disabled");
            formDiv.style.display = "block";
            formDiv.style.opacity = 1;
            formDiv.querySelector("input")?.removeAttribute("disabled");
          }
        }
      };

      const selectedSwatchInput = [...swatchInputs].find(
        (input) => input.checked
      );

      if (selectedSwatchInput) setSelectedOption(selectedSwatchInput);
      swatchInputs.forEach((input) => {
        input.addEventListener("click", (e) => {
          setSelectedOption(e.target);
        });
      });
      // Hide dropdown
      if (infiniteContainer.firstChild) {
        // Some infinite container can be disabled
        // Make sure to remove disabled feature to manually trigger select option

        var selector = infiniteContainer.firstChild.querySelector("select");
        if (selector) {
          selector.removeAttribute("disabled");
          selector.style.display = "none";
          infiniteContainer.firstChild.style.display = "none";
          infiniteContainer.firstChild.removeAttribute("disabled");
        }
      }
    }

    const callback = (mutationList, observer) => {
      mutationList.forEach((mutation) => {
        const childNode = mutation.addedNodes[0];
        // Only listen for file input changes for added files
        if (
          childNode &&
          childNode.tagName == "INPUT" &&
          childNode.name.includes("properties") &&
          !childNode.classList.contains("processed")
        ) {
          handleProductOptionOrder();
        }

        if (mutation && mutation.target.id == "infiniteoptions-container") {
          handleProductOptionDisplay();
        }
      });
    };
    const observer = new MutationObserver(callback);
    // Listen for changes on product form when visible
    if (productForm) {
      observer.observe(productForm, config);
    }
  })();

  (function () {
    // Handle ability to show rating before price in product page
    // This is a custom ability enabled through product page
    // Custom setting in sections/main-product.liquid: 'section.settings.show_rating_before_price'
    function showRatingBeforePrice() {
      var ratingBeforePrice = document.querySelector(
        "[data-show-rating-before-price]"
      );
      var looxRating = document.querySelector("[href='#looxReviews']");
      var productTitle = document.querySelector(".product-meta__title");
      if (!ratingBeforePrice || !looxRating || !productTitle) return;
      looxRating.parentNode.style.marginBottom = "20px";
      productTitle.parentNode.insertBefore(
        looxRating.parentNode,
        productTitle.nextSibling
      );
    }

    showRatingBeforePrice();
  })();
});
